import { useEffect, useState, type FormEvent } from "react";
import { ArrowRight, Check, Copy, Eye, EyeOff, KeyRound, LoaderCircle, ShieldCheck } from "lucide-react";
import { getRecoveryToken } from "../api";

interface AuthGateProps {
  busy: boolean;
  error: string;
  onLogin(token: string): Promise<void>;
}

export function AuthGate({ busy, error, onLogin }: AuthGateProps) {
  const [token, setToken] = useState("");
  const [tokenVisible, setTokenVisible] = useState(false);
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void getRecoveryToken().then((recoveredToken) => {
      if (!recoveredToken) return;
      setToken(recoveredToken);
      setRecoveryAvailable(true);
    });
  }, []);

  async function copyToken() {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!token.trim() || busy) return;
    await onLogin(token.trim());
  }

  return (
    <main className="auth-screen">
      <section className="auth-identity" aria-label="Nora">
        <div className="auth-brand">
          <span className="auth-mark nora-logo" aria-hidden="true" />
          <div className="auth-wordmark">
            <h1>Nora</h1>
            <span className="auth-eyebrow">Assistante personnelle</span>
          </div>
        </div>
        <div className="auth-trust">
          <ShieldCheck aria-hidden="true" />
          <span>Accès privé</span>
        </div>
      </section>
      <section className="auth-access">
        <form className="auth-panel" onSubmit={submit}>
          <header className="auth-panel-heading">
            <span>Identification</span>
            <h2>Connexion</h2>
            <p>Utilisez le token local de cette installation.</p>
          </header>
          <label className="field-label" htmlFor="auth-token">Token d’accès</label>
          <div className="auth-token-field">
            <KeyRound aria-hidden="true" />
            <input
              id="auth-token"
              type={tokenVisible ? "text" : "password"}
              value={token}
              onChange={(event) => setToken(event.target.value)}
              autoComplete="current-password"
              autoFocus
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => setTokenVisible((visible) => !visible)}
              disabled={busy}
              aria-label={tokenVisible ? "Masquer le token" : "Afficher le token"}
              title={tokenVisible ? "Masquer le token" : "Afficher le token"}
            >
              {tokenVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
          {recoveryAvailable ? (
            <button className="auth-copy-token" type="button" onClick={() => void copyToken()}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              <span>{copied ? "Token copié" : "Copier le token"}</span>
            </button>
          ) : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="auth-submit" type="submit" disabled={busy || !token.trim()} aria-label="Se connecter">
            <span>{busy ? "Connexion…" : "Ouvrir Nora"}</span>
            {busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
          </button>
        </form>
        <footer className="auth-footer">Natio · Espace personnel</footer>
      </section>
    </main>
  );
}
